import { useEffect, useState } from "react";
import { fetchMaterials, fetchPrinters } from "../api";
import { Fleet } from "../components/Fleet";
import type { MaterialProfile, PrinterProfile } from "../types";

import { useT } from "../i18n";
const MAT_COLOR: Record<string, string> = {
  PLA: "#d94f4f",
  PETG: "#2f9e8f",
  ABS: "#5b6273",
  RESIN_STANDARD: "#9fb7d4",
  WOOD_FILL: "#9c6b3f",
  METAL_FILL: "#b8bcc4",
};

function BuildVolume({ p }: { p: PrinterProfile }) {
  const [x, y, z] = p.build_volume_mm;
  const max = Math.max(x, y, z);
  return (
    <div className="row" style={{ gap: 16, alignItems: "flex-end", marginBottom: 14, height: 84 }}>
      <div
        className="blueprint"
        style={{
          position: "relative",
          flexShrink: 0,
          width: (x / max) * 70 + 30,
          height: (z / max) * 56 + 18,
          border: "1px solid var(--accent-line)",
          borderRadius: 6,
          background: "var(--accent-soft)",
        }}
      >
        <div style={{ position: "absolute", inset: 0, borderBottom: "2px solid var(--accent)", borderRadius: "0 0 6px 6px" }} />
      </div>
      <div className="num" style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.7, whiteSpace: "nowrap", flexShrink: 0 }}>
        <div>X <span style={{ color: "var(--muted)" }}>{Math.round(x)}</span> mm</div>
        <div>Y <span style={{ color: "var(--muted)" }}>{Math.round(y)}</span> mm</div>
        <div>Z <span style={{ color: "var(--muted)" }}>{Math.round(z)}</span> mm</div>
      </div>
    </div>
  );
}

export function PrintersPage() {
  const tr = useT();
  const [printers, setPrinters] = useState<PrinterProfile[]>([]);
  const [materials, setMaterials] = useState<MaterialProfile[]>([]);

  useEffect(() => {
    fetchPrinters().then(setPrinters).catch(() => {});
    fetchMaterials().then(setMaterials).catch(() => {});
  }, []);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Système")}</div>
          <h1>{tr("Imprimantes & matériaux")}</h1>
          <div className="sub">{tr("La ferme (réseau, statut live), les profils machine et les matières.")}</div>
        </div>
      </div>

      <Fleet />

      <h2 style={{ marginBottom: 14 }}>Profils machine</h2>
      <div className="grid cards" style={{ marginBottom: 28 }}>
        {printers.map((p) => (
          <div key={p.id} className="card">
            <div className="row between" style={{ marginBottom: 12 }}>
              <div className="card-title">{p.name}</div>
              {p.technology && <span className="badge accent">{p.technology.toUpperCase()}</span>}
            </div>
            <BuildVolume p={p} />
            <div className="row between" style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
              <span className="muted" style={{ fontSize: 12 }}>Buse</span>
              <span className="num" style={{ fontSize: 12 }}>{p.nozzle_diameter_mm != null ? `${p.nozzle_diameter_mm} mm` : "—"}</span>
            </div>
          </div>
        ))}
      </div>

      <h2 style={{ marginBottom: 14 }}>{tr("Matériaux")}</h2>
      <div className="grid cards">
        {materials.map((m) => {
          const color = MAT_COLOR[m.id] ?? "#7a8599";
          const family = m.type === "resin" ? "Résine" : "FDM";
          return (
            <div key={m.id} className="card">
              <div className="row between" style={{ marginBottom: 12 }}>
                <div className="row" style={{ gap: 11 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: color, boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.12), 0 4px 12px ${color}55` }} />
                  <span className="card-title">{m.name}</span>
                </div>
                <span className="badge">{m.preview_finish}</span>
              </div>
              <div className="row between" style={{ fontSize: 12 }}>
                <span className="muted">{family}</span>
                <span className="num muted">
                  {m.density_g_cm3 != null ? `${m.density_g_cm3} g/cm³` : ""}
                  {m.nozzle_temp_c ? ` · ${m.nozzle_temp_c}°C` : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
